import { SalesPackageModel, SalesPlanModel } from "@common/models";
import { IWellnessExamSalesItem } from "@common/models/forms";
import { makeAllBold } from "@common/utils";
import { action, computed, makeObservable, observable } from "mobx";
import { ISalesStore } from "@hospital/stores/SalesStore";

export type SalesBuilderSearchResults = {
    searchQueryApplied: boolean,
    queryPatterns: undefined | string[],
    totalRegularSalesItemsCount: number,
    regularSalesItems: IWellnessExamSalesItem[],
    totalDiscountedSalesItemCount: number,
    discountedSalesItems: IWellnessExamSalesItem[],
    totalSalesPackageCount: number,
    salesPackages: SalesPackageModel[],
    totalSalesPlanCount: number
    salesPlans: SalesPlanModel[]
}

export class SalesBuilderSearchStore {
    @observable salesStore!: ISalesStore;
    @observable _searchQuery: string | undefined = undefined;
    @observable searchQueryApplied: boolean = false;

    @computed get queryPatterns() {
        if (!this._searchQuery)
            return undefined;
        return this._searchQuery.trim().toLowerCase().split(" ").map(qp => qp.trim()).filter(qp => qp != "");
    }

    constructor(opts: ISalesStore) {
        this.salesStore = opts;

        makeObservable(this, undefined, { autoBind: true });
    }

    /**
    * Wraps up the regular sales items, discounted sales items, plans, and packages into one search results object.
    * @returns An object with the search query, whether it's applied, and all the arrays of sales items, packages, plans, and discounted sales items with or without the search query applied
    */
    @computed get searchResults() {
        let ret: SalesBuilderSearchResults = {
            searchQueryApplied: false,
            queryPatterns: this.queryPatterns,
            totalRegularSalesItemsCount: this.salesStore.totalRegularSalesItems,
            totalDiscountedSalesItemCount: this.salesStore.totalDiscountSalesItems,
            totalSalesPackageCount: this.salesStore.totalSalesPackageCount,
            totalSalesPlanCount: this.salesStore.totalSalesPlanCount,
            regularSalesItems: this.salesStore.regularSalesItems,
            discountedSalesItems: this.salesStore.discountedSalesItems,
            salesPackages: this.salesStore.salesPackages,
            salesPlans: this.salesStore.salesPlans
        };

        if (!this.queryPatterns)
            return ret;
        else {
            ret.searchQueryApplied = true;
            ret.regularSalesItems = this.salesStore.regularSalesItems.filter(si => this.queryPatterns!.every(q => si.searchPattern.includes(q))).map(si => {
                return { ...si, name: makeAllBold(si.name, this.queryPatterns!), option: makeAllBold(si.option, this.queryPatterns!) };
            });
            ret.discountedSalesItems = this.salesStore.discountedSalesItems.filter(si => this.queryPatterns!.every(q => si.searchPattern.includes(q))).map(si => {
                return { ...si, name: makeAllBold(si.name, this.queryPatterns!), option: makeAllBold(si.option, this.queryPatterns!) };
            });
            ret.salesPlans = this.salesStore.salesPlans.filter(sp => this.queryPatterns!.every(q => (sp.name ? sp.name.toLowerCase() : '').includes(q))).map(sp => {
                return new SalesPlanModel({ ...sp, name: makeAllBold(sp.name, this.queryPatterns!) });
            });
            ret.salesPackages = this.salesStore.salesPackages.filter(sp => this.queryPatterns!.every(q => (`${sp.name} ${sp.displayName}`.toLowerCase().includes(q)))).map(sp => {
                return new SalesPackageModel({ ...sp, name: makeAllBold(sp.name, this.queryPatterns!), displayName: makeAllBold(sp.displayName, this.queryPatterns!) });
            });
            return ret;
        }
    }

    @action.bound setSearchQuery(searchQuery: undefined | string) {
        this._searchQuery = searchQuery;
    }
}
